import { Ionicons } from '@expo/vector-icons';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { useWindowDimensions, View } from 'react-native';

import { MetricCard } from '@/components/dashboard/metric-card';
import { DateRangePicker } from '@/components/forms/date-range-picker';
import { PaymentNoticeBanner } from '@/components/payments/payment-notice-banner';
import { ReceivablesEvolutionCard } from '@/components/payments/receivables-evolution-card';
import { ReceivablesPeriodHero } from '@/components/payments/receivables-period-hero';
import { ScheduleDayRow } from '@/components/payments/schedule-day-row';
import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { FilterSelect, type FilterSelectOption } from '@/components/ui/filter-select';
import { PageLayout } from '@/components/ui/page-layout';
import { Pagination } from '@/components/ui/pagination';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SectionCard } from '@/components/ui/section-card';
import { SkeletonListRow, SkeletonSummaryCard } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  ACTIVATION_STATE_ACTION_REQUIRED,
  ACTIVATION_STATE_DISABLED,
  ACTIVATION_STATE_RESTRICTED,
  formatPayoutAccount,
  PAYOUT_STATUS_LABELS,
  PAYOUT_STATUS_VARIANTS,
  RECEIVABLES_EMPTY,
  RECEIVABLES_NOTICES,
  RECEIVABLES_PAYOUTS,
  RECEIVABLES_PERIOD,
  RECEIVABLES_SCHEDULE,
  RECEIVABLES_SCREEN,
  RECEIVABLES_TOTALS,
} from '@/content/payments';
import { useMarkNotificationAsRead } from '@/hooks/use-mark-notification-read';
import { usePaymentAccount } from '@/hooks/use-payment-account';
import { usePaymentPayouts } from '@/hooks/use-payment-payouts';
import {
  usePaymentBalance,
  usePaymentSchedule,
  usePaymentSummary,
} from '@/hooks/use-payment-reporting';
import { useProviderNotifications } from '@/hooks/use-provider-notifications';
import { showToast } from '@/store/toastStore';
import { PAYMENT_ACCOUNT_STATUS, type PaymentAccount } from '@/types/payment-account';
import { PaymentPayout, PROVIDER_PAYOUT_STATUS } from '@/types/payment-payout';
import type { PaymentBalance, PaymentScheduleDay } from '@/types/payment-reporting';
import { ProviderNotification } from '@/types/provider-notification';
import { getApiErrorMessage } from '@/utils/api-error';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';

const PAYOUTS_PAGE_SIZE = 10;
/** A partir desta largura, hero ao lado dos saldos e agenda ao lado dos repasses. */
const WIDE_BREAKPOINT = 900;
/** Abaixo desta largura, `MetricCard` compacto (mesmo corte da Home). */
const COMPACT_BREAKPOINT = 500;

type ReceivablesPeriod = '7d' | '30d' | '90d' | 'all' | 'custom';
const PERIOD_ORDER: ReceivablesPeriod[] = ['7d', '30d', '90d', 'all', 'custom'];
/** Mesmo teto de `GET /payment-account/summary` (`SUMMARY_MAX_RANGE_DAYS`, `reporting-calculations.ts`) — sem isso, um período `custom` maior vira 400 `SUMMARY_RANGE_TOO_LONG`. */
const SUMMARY_MAX_RANGE_DAYS = 366;

function goBack() {
  if (router.canGoBack()) {
    router.back();
    return;
  }
  router.replace('/(tabs)/(provider)/home' as never);
}

function goToActivation() {
  router.push('/(tabs)/(provider)/payments/activate' as never);
}

function goToSettings() {
  router.push('/(tabs)/(provider)/payments/settings' as never);
}

function goToReleasing() {
  router.push('/(tabs)/(provider)/payments/releasing' as never);
}

function goToPayout(id: string) {
  router.push(`/(tabs)/(provider)/payments/payouts/${id}` as never);
}

/** Primeiro e último dia de um período (o `all` é o último ano, teto do `summary`). */
function periodRange(
  period: ReceivablesPeriod,
  customRange: { start: Date; end: Date },
): { start: Date; end: Date } {
  const end = endOfDay(new Date());
  if (period === 'custom') {
    return { start: startOfDay(customRange.start), end: endOfDay(customRange.end) };
  }
  if (period === 'all') return { start: startOfDay(subDays(end, 365)), end };
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
  return { start: startOfDay(subDays(end, days - 1)), end };
}

/** "18 a 24 de set" · "26 de ago a 24 de set" · "24 de set de 2025 a 24 de set de 2026". */
function formatRange({ start, end }: { start: Date; end: Date }): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  const endLabel = format(end, sameYear ? "d 'de' MMM" : "d 'de' MMM 'de' yyyy", { locale: ptBR });
  const startLabel = format(
    start,
    sameMonth ? 'd' : sameYear ? "d 'de' MMM" : "d 'de' MMM 'de' yyyy",
    { locale: ptBR },
  );
  return `${startLabel} a ${endLabel}`;
}

/** Status HTTP de um erro de API, sem acoplar ao tipo do axios. */
function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * Recebimentos (T6.1, PLANO 6.4): saldo, agenda, repasses e resumo do
 * período de quem já ativou o recebimento automático. Conta sem ativação —
 * ou ainda `DRAFT`/`VERIFYING`, sem nada para mostrar — vai direto para
 * `payments/activate.tsx` (T4.5), que já cobre esses dois estados.
 */
export default function PaymentsScreen() {
  const { data: statusData, isLoading, isFetched } = usePaymentAccount();
  const account = statusData?.account ?? null;
  const needsActivation =
    isFetched &&
    (!account ||
      account.status === PAYMENT_ACCOUNT_STATUS.DRAFT ||
      account.status === PAYMENT_ACCOUNT_STATUS.VERIFYING);

  useEffect(() => {
    if (needsActivation) {
      router.replace('/(tabs)/(provider)/payments/activate' as never);
    }
  }, [needsActivation]);

  if (isLoading || !isFetched || needsActivation || !account) {
    return (
      <PageLayout title="Recebimentos" onBack={goBack}>
        <SkeletonSummaryCard />
        <SkeletonListRow />
        <SkeletonListRow />
      </PageLayout>
    );
  }

  return <ReceivablesScreen account={account} />;
}

function ReceivablesScreen({ account }: { account: PaymentAccount }) {
  const [page, setPage] = useState(1);
  const [period, setPeriod] = useState<ReceivablesPeriod>('30d');
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date }>({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const compact = width > 0 && width < COMPACT_BREAKPOINT;

  // `GET /payment-account/summary` recusa (400 `SUMMARY_RANGE_TOO_LONG`) um
  // intervalo maior que `SUMMARY_MAX_RANGE_DAYS` — quem barra é este
  // handler, nunca deixando o estado inválido existir.
  const handleCustomRangeChange = (range: { start: Date; end: Date }) => {
    const spanDays = Math.round(
      (startOfDay(range.end).getTime() - startOfDay(range.start).getTime()) / 86_400_000,
    );
    if (spanDays > SUMMARY_MAX_RANGE_DAYS) {
      setCustomRange({ start: subDays(range.end, SUMMARY_MAX_RANGE_DAYS), end: range.end });
      showToast(`Escolha um período de até ${SUMMARY_MAX_RANGE_DAYS} dias.`, 'info');
      return;
    }
    setCustomRange(range);
  };

  const periodBounds = useMemo(() => {
    const range = periodRange(period, customRange);
    return {
      from: format(range.start, 'yyyy-MM-dd'),
      to: format(range.end, 'yyyy-MM-dd'),
      label: formatRange(range),
    };
  }, [period, customRange]);

  const periodOptions = useMemo<FilterSelectOption<ReceivablesPeriod>[]>(
    () =>
      PERIOD_ORDER.map((value) => ({
        value,
        label: RECEIVABLES_PERIOD.options[value],
        hint:
          value === 'custom' && period !== 'custom'
            ? RECEIVABLES_PERIOD.customHint
            : formatRange(periodRange(value, customRange)),
      })),
    [period, customRange],
  );

  const balance = usePaymentBalance();
  const schedule = usePaymentSchedule();
  const payouts = usePaymentPayouts({ page, limit: PAYOUTS_PAGE_SIZE });
  const summary = usePaymentSummary(periodBounds.from, periodBounds.to);
  const notifications = useProviderNotifications(20);
  const markAsRead = useMarkNotificationAsRead();

  const forbiddenError = [balance.error, schedule.error, payouts.error, summary.error].find(
    (error) => errorStatus(error) === 403,
  );

  if (forbiddenError) {
    return (
      <PageLayout title="Recebimentos" onBack={goBack}>
        <ErrorState
          icon="lock-closed-outline"
          title="Sem acesso"
          message={getApiErrorMessage(
            forbiddenError,
            'Você não pode ver os recebimentos desta conta.',
          )}
        />
      </PageLayout>
    );
  }

  const openNotice = (notification?: ProviderNotification) => {
    if (notification) markAsRead.mutate(notification.id);
    goToActivation();
  };

  const payoutFailedNotice = notifications.data?.find(
    (notification) => notification.type === 'PAYMENT_PAYOUT_FAILED' && !notification.readAt,
  );
  const disputeNotice = notifications.data?.find(
    (notification) => notification.type === 'DISPUTE_CREATED' && !notification.readAt,
  );

  const payoutsData = payouts.data?.data ?? [];
  const totalPayouts = payouts.data?.meta.total ?? 0;
  const totalPayoutPages = payouts.data?.meta.totalPages ?? 1;
  const inTransitPayout = payoutsData.find(
    (payout) => payout.status === PROVIDER_PAYOUT_STATUS.IN_TRANSIT,
  );
  const payoutCount = summary.data?.payoutCount;
  const periodSummary =
    payoutCount == null
      ? periodBounds.label
      : `${periodBounds.label} · ${payoutCount} ${payoutCount === 1 ? 'repasse' : 'repasses'}`;
  const column = wide ? 'min-w-0 flex-1 gap-4' : 'gap-4';

  return (
    <PageLayout
      title={RECEIVABLES_SCREEN.title}
      subtitle={RECEIVABLES_SCREEN.subtitle}
      onBack={goBack}
      headerAction={
        <PressableScale
          haptic
          onPress={goToSettings}
          accessibilityRole="button"
          accessibilityLabel="Configurar recebimento"
          className="h-10 w-10 items-center justify-center rounded-full bg-card"
          style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
        >
          <Ionicons className="text-neutral-900" name="settings-outline" size={19} />
        </PressableScale>
      }
    >
      {account.status === PAYMENT_ACCOUNT_STATUS.ACTION_REQUIRED ? (
        <PaymentNoticeBanner
          tone="warning"
          title={ACTIVATION_STATE_ACTION_REQUIRED.title}
          body={ACTIVATION_STATE_ACTION_REQUIRED.body}
          actionLabel={ACTIVATION_STATE_ACTION_REQUIRED.resumeButton}
          onPress={() => openNotice()}
          testID="payments-notice-action-required"
        />
      ) : null}

      {account.status === PAYMENT_ACCOUNT_STATUS.RESTRICTED ? (
        <PaymentNoticeBanner
          tone="error"
          title={ACTIVATION_STATE_RESTRICTED.title}
          body={account.requirementsDue?.disabledReason ?? ACTIVATION_STATE_RESTRICTED.body}
          onPress={() => openNotice()}
          testID="payments-notice-restricted"
        />
      ) : null}

      {account.status === PAYMENT_ACCOUNT_STATUS.DISABLED ? (
        <PaymentNoticeBanner
          tone="info"
          title={ACTIVATION_STATE_DISABLED.title}
          body={ACTIVATION_STATE_DISABLED.body}
          onPress={() => openNotice()}
          testID="payments-notice-disabled"
        />
      ) : null}

      {payoutFailedNotice ? (
        <PaymentNoticeBanner
          tone="error"
          title={RECEIVABLES_NOTICES.payoutFailedTitle}
          body={RECEIVABLES_NOTICES.payoutFailedBody}
          onPress={() => openNotice(payoutFailedNotice)}
          testID="payments-notice-payout-failed"
        />
      ) : null}

      {disputeNotice ? (
        <PaymentNoticeBanner
          tone="warning"
          title={RECEIVABLES_NOTICES.disputeOpenTitle}
          body={RECEIVABLES_NOTICES.disputeOpenBody}
          onPress={() => openNotice(disputeNotice)}
          testID="payments-notice-dispute"
        />
      ) : null}

      <View className="gap-3">
        <FilterSelect
          label={RECEIVABLES_PERIOD.title}
          sheetTitle={RECEIVABLES_PERIOD.sheetTitle}
          options={periodOptions}
          value={period}
          onChange={setPeriod}
          summary={periodSummary}
        />
        {period === 'custom' ? (
          <DateRangePicker
            startDate={customRange.start}
            endDate={customRange.end}
            onChange={handleCustomRangeChange}
          />
        ) : null}
      </View>

      <View className={wide ? 'flex-row items-stretch gap-4' : 'gap-4'}>
        <View className={column}>
          {summary.isLoading ? (
            <SkeletonSummaryCard />
          ) : summary.error || !summary.data ? (
            <ErrorState
              variant="inline"
              title="Não foi possível carregar o resumo"
              message={getApiErrorMessage(summary.error, 'Tente de novo em instantes.')}
              onRetry={() => summary.refetch()}
            />
          ) : (
            <FadeInView delay={0} className={wide ? 'flex-1' : undefined}>
              <ReceivablesPeriodHero
                fill={wide}
                summary={summary.data}
                periodLabel={RECEIVABLES_PERIOD.options[period]}
              />
            </FadeInView>
          )}
        </View>
        <View className={column}>
          <BalanceMetrics
            isLoading={balance.isLoading}
            error={balance.error}
            data={balance.data}
            payoutCount={payoutCount}
            inTransitPayoutId={inTransitPayout?.id}
            compact={compact}
            onRetry={() => balance.refetch()}
          />
        </View>
      </View>

      {summary.data ? (
        <FadeInView delay={120}>
          <ReceivablesEvolutionCard summary={summary.data} />
        </FadeInView>
      ) : null}

      <View className={wide ? 'flex-row items-start gap-4' : 'gap-4'}>
        <View className={column}>
          <ScheduleSection
            isLoading={schedule.isLoading}
            error={schedule.error}
            days={schedule.data?.days}
            onRetry={() => schedule.refetch()}
          />
        </View>
        <View className={column}>
          <PayoutsSection
            payouts={payoutsData}
            isLoading={payouts.isLoading}
            error={payouts.error}
            page={page}
            totalPages={totalPayoutPages}
            total={totalPayouts}
            onPageChange={setPage}
            onRetry={() => payouts.refetch()}
          />
        </View>
      </View>
    </PageLayout>
  );
}

/**
 * Os saldos de agora numa grade 2×2 de `MetricCard` (mesma grade da Home):
 * liberando e a caminho abrem o detalhe; o número de repasses é do período.
 */
function BalanceMetrics({
  isLoading,
  error,
  data,
  payoutCount,
  inTransitPayoutId,
  compact,
  onRetry,
}: {
  isLoading: boolean;
  error: unknown;
  data: PaymentBalance | undefined;
  payoutCount: number | undefined;
  inTransitPayoutId: string | undefined;
  compact: boolean;
  onRetry: () => void;
}) {
  if (isLoading) return <SkeletonSummaryCard />;
  if (error || !data) {
    return (
      <ErrorState
        variant="inline"
        title="Não foi possível carregar o saldo"
        message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
        onRetry={onRetry}
      />
    );
  }

  const item = 'min-w-[45%] flex-1';

  return (
    <View className="flex-row flex-wrap gap-3">
      <PressableScale
        haptic
        onPress={goToReleasing}
        accessibilityRole="button"
        accessibilityLabel={`${RECEIVABLES_TOTALS.releasing.title}: ${formatCurrency(data.pending)}. Ver quando cada pagamento libera`}
        className={item}
        testID="balance-releasing-tile"
      >
        <MetricCard
          icon="time-outline"
          value={formatCurrency(data.pending)}
          title={RECEIVABLES_TOTALS.releasing.title}
          compact={compact}
        />
      </PressableScale>
      {inTransitPayoutId ? (
        <PressableScale
          haptic
          onPress={() => goToPayout(inTransitPayoutId)}
          accessibilityRole="button"
          accessibilityLabel={`${RECEIVABLES_TOTALS.incoming.title}: ${formatCurrency(data.inTransit)}. Ver o repasse`}
          className={item}
          testID="balance-in-transit-tile"
        >
          <MetricCard
            icon="paper-plane-outline"
            value={formatCurrency(data.inTransit)}
            title={RECEIVABLES_TOTALS.incoming.title}
            compact={compact}
          />
        </PressableScale>
      ) : (
        <View className={item}>
          <MetricCard
            icon="paper-plane-outline"
            value={formatCurrency(data.inTransit)}
            title={RECEIVABLES_TOTALS.incoming.title}
            compact={compact}
          />
        </View>
      )}
      <View className={item}>
        <MetricCard
          icon="checkmark-circle-outline"
          value={formatCurrency(data.receivedThisMonth)}
          title={RECEIVABLES_TOTALS.receivedThisMonth.title}
          compact={compact}
        />
      </View>
      <View className={item}>
        <MetricCard
          icon="arrow-down-circle-outline"
          value={payoutCount == null ? '—' : String(payoutCount)}
          title={RECEIVABLES_PERIOD.payoutsLabel}
          compact={compact}
        />
      </View>
    </View>
  );
}

function ScheduleSection({
  isLoading,
  error,
  days,
  onRetry,
}: {
  isLoading: boolean;
  error: unknown;
  days: PaymentScheduleDay[] | undefined;
  onRetry: () => void;
}) {
  const [openDays, setOpenDays] = useState<Set<string>>(() => new Set());
  const toggleDay = (date: string) =>
    setOpenDays((current) => {
      const next = new Set(current);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  if (isLoading) return <SkeletonListRow />;
  if (error || !days) {
    return (
      <ErrorState
        variant="inline"
        title="Não foi possível carregar a agenda"
        message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
        onRetry={onRetry}
      />
    );
  }

  return (
    <SectionCard
      icon="calendar-outline"
      title={RECEIVABLES_SCHEDULE.title}
      badge={
        days.length > 0
          ? formatCurrency(days.reduce((sum, day) => sum + day.netAmount, 0))
          : undefined
      }
      badgeTone="warning"
    >
      {days.length === 0 ? (
        <ThemedText className="text-sm text-neutral-500">
          {RECEIVABLES_SCHEDULE.emptyState}
        </ThemedText>
      ) : (
        <>
          <ThemedText className="text-xs leading-4 text-neutral-500">
            {RECEIVABLES_SCHEDULE.hint}
          </ThemedText>
          <View className="gap-2">
            {days.map((day) => (
              <ScheduleDayRow
                key={day.date}
                day={day}
                expanded={openDays.has(day.date)}
                onToggle={() => toggleDay(day.date)}
              />
            ))}
          </View>
        </>
      )}
      <PressableScale
        haptic
        onPress={goToReleasing}
        accessibilityRole="link"
        className="flex-row items-center gap-1 self-start"
        testID="schedule-see-all-releasing"
      >
        <ThemedText className="font-semibold text-sm text-primary">
          {RECEIVABLES_SCHEDULE.seeAll}
        </ThemedText>
        <Ionicons name="chevron-forward" size={14} color="#FD5001" />
      </PressableScale>
    </SectionCard>
  );
}

function PayoutsSection({
  payouts,
  isLoading,
  error,
  page,
  totalPages,
  total,
  onPageChange,
  onRetry,
}: {
  payouts: PaymentPayout[];
  isLoading: boolean;
  error: unknown;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{RECEIVABLES_PAYOUTS.title}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <View className="gap-2">
              <SkeletonListRow />
              <SkeletonListRow />
            </View>
          ) : error ? (
            <ErrorState
              variant="inline"
              title="Não foi possível carregar os repasses"
              message={getApiErrorMessage(error, 'Tente de novo em instantes.')}
              onRetry={onRetry}
            />
          ) : payouts.length === 0 ? (
            <EmptyState
              icon="cash-outline"
              title={RECEIVABLES_EMPTY.title}
              description={RECEIVABLES_EMPTY.body}
            />
          ) : (
            <View>
              {payouts.map((payout, index) => (
                <FadeInView key={payout.id} delay={index * 30}>
                  <PressableScale
                    haptic
                    accessibilityRole="button"
                    onPress={() => goToPayout(payout.id)}
                    className={cn(
                      'flex-row items-center gap-3 py-3.5',
                      index < payouts.length - 1 && 'border-b border-neutral-100',
                    )}
                    testID={`payout-row-${payout.id}`}
                  >
                    <View className="h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
                      <Ionicons className="text-primary dark:text-white" name="arrow-down-circle-outline" size={18} />
                    </View>

                    <View className="min-w-0 flex-1 gap-0.5">
                      <ThemedText
                        className="font-semibold text-sm leading-tight text-foreground"
                        numberOfLines={1}
                      >
                        {formatDate(payout.arrivalDate ?? payout.createdAt)}
                      </ThemedText>
                      <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
                        {formatPayoutAccount(null, payout.bankLast4)}
                      </ThemedText>
                    </View>

                    <View className="shrink-0 flex-row items-center gap-2">
                      <View className="items-end gap-1">
                        <ThemedText
                          className="font-bold text-sm text-foreground"
                          style={{ fontVariant: ['tabular-nums'] }}
                        >
                          {formatCurrency(payout.amount)}
                        </ThemedText>
                        <StatusBadge
                          status={{
                            type: 'custom',
                            value: payout.status,
                            label: PAYOUT_STATUS_LABELS[payout.status],
                            variant: PAYOUT_STATUS_VARIANTS[payout.status],
                          }}
                          size="xs"
                        />
                      </View>
                      <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
                    </View>
                  </PressableScale>
                </FadeInView>
              ))}
            </View>
          )}
        </CardContent>
      </Card>

      {!isLoading && !error && total > PAYOUTS_PAGE_SIZE ? (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
      ) : null}
    </>
  );
}
